import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_BASE = process.env.REACT_APP_API_URL;

// Inserts text at cursor position in a textarea and returns new content
function insertAtCursor(el, content, snippet) {
  const start = el.selectionStart;
  const end = el.selectionEnd;
  return {
    newContent: content.substring(0, start) + snippet + content.substring(end),
    newCursor: start + snippet.length,
  };
}

const SNIPPETS = {
  section: (n) => `\n## ${n}. Section Title\n\nSection content here.\n`,
  requirement: () =>
    `\n> **REQ:** Requirement title\n>\n> Requirement text: describe what must be met.\n>\n> **Severity:** Major | **Type:** manual | **Evidence required:** Yes\n`,
  table: () =>
    `\n| Column 1 | Column 2 | Column 3 |\n|---|---|---|\n| Value | Value | Value |\n`,
};

export default function QualityDocumentEditor() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  const role = String(user?.publicMetadata?.role || '').toLowerCase().trim();
  const canContribute = ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role)
    || ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(role.replace(/^test-/, ''));
  if (!canContribute) {
    navigate('/quality');
  }

  const [doc, setDoc] = useState(null);
  const [revision, setRevision] = useState(null);
  const [content, setContent] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('split');
  const textareaRef = useRef(null);
  const autosaveTimer = useRef(null);
  const contentRef = useRef(content);
  const changeSummaryRef = useRef(changeSummary);
  contentRef.current = content;
  changeSummaryRef.current = changeSummary;

  const apiCall = useCallback(async (path, method = 'GET', body = null) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return res.json();
  }, [getToken]);

  const load = useCallback(async () => {
    const docData = await apiCall(`/quality/documents/${id}`);
    if (!docData.ok) { setError('Document not found'); setLoading(false); return; }
    setDoc(docData.document);

    const revsData = await apiCall(`/quality/documents/${id}/revisions`);
    const revsAll = revsData.revisions || [];
    const draft = revsAll.find(r => r.status === 'Draft' || r.status === 'In Review');
    if (!draft) {
      setError('No open draft revision found. Create a new revision first.');
      setLoading(false);
      return;
    }
    const revData = await apiCall(`/quality/documents/${id}/revisions/${draft._id}`);
    if (revData.ok) {
      setRevision(revData.revision);
      setContent(revData.revision.markdownContent || '');
      setChangeSummary(revData.revision.changeSummary || '');
    }
    setLoading(false);
  }, [apiCall, id]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (isAutosave = false) => {
    if (!revision || revision.locked) return;
    setSaving(true);
    const d = await apiCall(`/quality/documents/${id}/revisions/${revision._id}`, 'PATCH', {
      markdownContent: contentRef.current,
      changeSummary: changeSummaryRef.current,
    });
    setSaving(false);
    if (d.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } else if (!isAutosave) {
      setError(d.message || 'Save failed');
    }
  }, [apiCall, id, revision]);

  // Autosave 3s after last keystroke
  useEffect(() => {
    if (!revision || revision.locked) return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => save(true), 3000);
    return () => clearTimeout(autosaveTimer.current);
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      save(false);
    }
  };

  const insert = useCallback((snippetFn, ...args) => {
    const el = textareaRef.current;
    if (!el) return;
    const snippet = snippetFn(...args);
    const { newContent, newCursor } = insertAtCursor(el, content, snippet);
    setContent(newContent);
    requestAnimationFrame(() => {
      el.selectionStart = newCursor;
      el.selectionEnd = newCursor;
      el.focus();
    });
  }, [content]);

  // Count existing h2 sections to auto-number the next one
  const nextSectionNumber = () => {
    const matches = content.match(/^## \d+\./gm);
    return (matches ? matches.length : 0) + 1;
  };

  if (loading) return <div style={{ color: 'var(--ni-text-muted)', padding: 40 }}>Loading...</div>;
  if (error) return (
    <div style={{ padding: 40 }}>
      <div style={{ color: '#EC2124', marginBottom: 16 }}>{error}</div>
      <button onClick={() => navigate(`/quality/documents/${id}`)} style={{ background: 'none', border: 'none', color: 'var(--ni-green)', cursor: 'pointer' }}>
        ← Back to document
      </button>
    </div>
  );
  if (!doc || !revision) return null;

  const isLocked = revision.locked;

  const toolbarBtnStyle = (active = false) => ({
    background: active ? 'rgba(70,187,137,0.15)' : 'none',
    border: '1px solid var(--ni-border-soft)',
    color: active ? 'var(--ni-green)' : 'var(--ni-text-muted)',
    borderRadius: 5,
    padding: '4px 11px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.02em',
  });

  return (
    <div style={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>

      {/* Top bar: breadcrumb + view toggle + save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(`/quality/documents/${id}`)}
          style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', cursor: 'pointer', fontSize: 13, padding: 0 }}
        >
          ← {doc.documentNumber}
        </button>
        <span style={{ color: 'var(--ni-border-soft)' }}>|</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--ni-text)' }}>{doc.title}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ni-green)', background: 'rgba(70,187,137,0.1)', padding: '2px 8px', borderRadius: 4 }}>
          Rev. {revision.revision}
        </span>
        {isLocked && <span style={{ color: '#EC2124', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em' }}>LOCKED</span>}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* View mode */}
          <div style={{ display: 'flex', background: 'var(--ni-panel)', borderRadius: 6, padding: 2, border: '1px solid var(--ni-border-soft)' }}>
            {['edit', 'split', 'preview'].map(m => (
              <button
                key={m}
                onClick={() => setViewMode(m)}
                style={{
                  background: viewMode === m ? 'rgba(70,187,137,0.15)' : 'transparent',
                  border: 'none',
                  color: viewMode === m ? 'var(--ni-green)' : 'var(--ni-text-muted)',
                  borderRadius: 4,
                  padding: '4px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {m}
              </button>
            ))}
          </div>

          {saved && <span style={{ color: 'var(--ni-green)', fontSize: 12 }}>Saved ✓</span>}
          {saving && <span style={{ color: 'var(--ni-text-muted)', fontSize: 12 }}>Saving…</span>}

          {!isLocked && (
            <button
              onClick={() => save(false)}
              disabled={saving}
              style={{ background: 'var(--ni-green)', color: '#000', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Save
            </button>
          )}
        </div>
      </div>

      {/* Change summary */}
      {!isLocked && (
        <div style={{ marginBottom: 8 }}>
          <input
            type="text"
            placeholder="Change summary — describe what changed in this revision (required before release)"
            value={changeSummary}
            onChange={e => setChangeSummary(e.target.value)}
            style={{
              width: '100%',
              background: 'var(--ni-panel)',
              border: `1px solid ${changeSummary.trim() ? 'var(--ni-border-soft)' : '#f59e0b88'}`,
              color: 'var(--ni-text)',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      {/* Insert toolbar — only visible in edit or split mode */}
      {!isLocked && (viewMode === 'edit' || viewMode === 'split') && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--ni-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>Insert:</span>
          <button style={toolbarBtnStyle()} onClick={() => insert(SNIPPETS.section, nextSectionNumber())}>
            + Section
          </button>
          <button style={toolbarBtnStyle()} onClick={() => insert(SNIPPETS.requirement)}>
            + Requirement
          </button>
          <button style={toolbarBtnStyle()} onClick={() => insert(SNIPPETS.table)}>
            + Table
          </button>
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ni-text-muted)' }}>Ctrl+S to save</span>
        </div>
      )}

      {/* Editor area */}
      <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>
        {/* Edit pane */}
        {(viewMode === 'edit' || viewMode === 'split') && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 5 }}>Markdown</div>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={e => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLocked}
              placeholder={isLocked ? 'This revision is locked and cannot be edited.' : 'Write Markdown here…'}
              style={{
                flex: 1,
                width: '100%',
                background: 'var(--ni-panel)',
                border: '1px solid var(--ni-border-soft)',
                color: 'var(--ni-text-soft)',
                borderRadius: 8,
                padding: '16px',
                fontSize: 13,
                fontFamily: 'monospace',
                lineHeight: 1.6,
                resize: 'none',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Preview pane */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 5 }}>Preview</div>
            <div style={{
              flex: 1, overflow: 'auto',
              background: 'var(--ni-panel)',
              border: '1px solid var(--ni-border-soft)',
              borderRadius: 8,
              padding: '24px 28px',
            }}>
              {content ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {content}
                </ReactMarkdown>
              ) : (
                <div style={{ color: 'var(--ni-text-muted)', fontStyle: 'italic', fontSize: 13 }}>Nothing to preview yet.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const mdComponents = {
  h1: ({ children }) => <h1 style={{ fontSize: 20, fontWeight: 800, borderBottom: '1px solid var(--ni-border-soft)', paddingBottom: 10, marginTop: 0 }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 16 }}>{children}</h3>,
  p: ({ children }) => <p style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ni-text-soft)', margin: '0 0 10px' }}>{children}</p>,
  blockquote: ({ children }) => (
    <blockquote style={{ borderLeft: '3px solid var(--ni-green)', margin: '12px 0', padding: '10px 16px', background: 'rgba(70,187,137,0.06)', borderRadius: '0 6px 6px 0' }}>
      {children}
    </blockquote>
  ),
  table: ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 14 }}>{children}</table>,
  th: ({ children }) => <th style={{ padding: '6px 10px', background: 'rgba(0,0,0,0.2)', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', border: '1px solid var(--ni-border-soft)' }}>{children}</th>,
  td: ({ children }) => <td style={{ padding: '6px 10px', border: '1px solid var(--ni-border-soft)', fontSize: 12 }}>{children}</td>,
  code: ({ children }) => <code style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 4, padding: '2px 5px', fontSize: 11, fontFamily: 'monospace', color: 'var(--ni-green)' }}>{children}</code>,
  ul: ({ children }) => <ul style={{ paddingLeft: 18, marginBottom: 10 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ paddingLeft: 18, marginBottom: 10 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 3, fontSize: 13, color: 'var(--ni-text-soft)' }}>{children}</li>,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid var(--ni-border-soft)', margin: '16px 0' }} />,
  strong: ({ children }) => <strong style={{ fontWeight: 700, color: 'var(--ni-text)' }}>{children}</strong>,
};
